import { MenuNameOverrideMap } from '../../constants/role-access-map.constant';
import { MenuItem } from '../../interfaces/menu-item.interface';
import {
  PermissionAction,
  PermissionNode,
  TabConfig,
} from '../../interfaces/permission.model';

// Default actions
const DEFAULT_ACTIONS = ['View', 'Create', 'Edit', 'Delete'];
const ACTION_SETS = {
  CRUD: ['View', 'Create', 'Edit', 'Delete'],
  VIEW_ONLY: ['View'],
  ENROLLE_CRUD: ['Create', 'View', 'Edit', 'Change Status'],
    APPROVAL_FLOW: ['View', 'Approve', 'Reject'],
  SUSPENSION_FLOW: ['View', 'Reactivate'],
  REFERRAL_ACTIONS_CONFIG: ['View', 'Comment', 'Appeal', 'Renew', 'Accept', 'Reject'],
};

// { name: 'HBP', key: 'catalog-hbp', actions: ACTION_SETS.VIEW_ONLY }
// Custom per key
const MODULE_ACTION_MAP: Record<string, string[]> = {
  dashboard: ['View'],
  catalog: [],
  program: [],
  pricing: ['Blink'],
  claims: ['View', 'Review', 'Approve', 'Reject'],
  'billing-payment': ['View', 'Process', 'Refund'],
  'bhcpf-enrollee': ['Create', 'View', 'Edit', 'Change Status'],
  'membership-synchronization': ['Upload', 'View',],
  'dependent-enrollee': ['Create', 'View', 'Edit', 'Deactivate', 'Transfer'],
  'employer-group': [
    'Create',
    'View',
    'Edit',
    'Delete',
    'Approve',
    'Reject',
    'Reinstate',
    'Renew',
    'Archive',
  ],
  'self-enrollee': ACTION_SETS.ENROLLE_CRUD,
  'provider-management': ['View', 'Create', 'Edit', 'Link Provider'],
  'accreditation': ['Create', 'View', 'Inspection', 'Inspection Scheduling', 'Add note', 'Verify Evidence', 'RFI', "Make Decision"],
  'pre-authorization': ['View', 'Edit', 'Download'],
  'authorization-queues': ['View', 'Assign', 'Request Info', 'Quick Approve', 'Quick Decline', 'Reassign', 'Generate Code', 'Reject', 'Continue Review', 'Extend', 'Reopen'],
  'authorization-code-manager': ['View Code', 'Extend', 'Revoke', 'Print', 'View Case'],
'referral-management': ACTION_SETS.REFERRAL_ACTIONS_CONFIG,
'add-new-referral': ACTION_SETS.REFERRAL_ACTIONS_CONFIG,
'claims-management': ACTION_SETS.VIEW_ONLY,
'claims-review-validation': [
  'View',
  'Send To Adjudication',
  'Partial Approval',
  'Escalate',
  'Reject',
  'Reopen',
  'Approve For Adjudication',
  'Return To New',
],
'claims-adjudication': [
  'View',
  'Make Payment',
  'Reject',
],
'claims-document-review': [
  'View',
  'Verify Document',
  'Reject Document',
  'Upload Missing Documents',
  'Flag For Fraud Check',
  'Link To Claim',
  'Export Documents',
  'Audit',
],
'ai-claims-vetting': [
  'View',
  'Run Vetting',
  'Assign Reviewer',
  'Reject',
  'Audit',
],
'claims-report-analytics': [
  'View',
  'Generate Report',
  'Schedule Report',
  'Drill Down',
  'Fraud Flagging',
],
'reports-analytics': [ 'View', 'Generate Report',],
'encounters-referrals': ACTION_SETS.VIEW_ONLY,
'encounter': ACTION_SETS.VIEW_ONLY,
// 'capitation': ['View', 'Create', 'Edit', 'Delete'],
'refunds-adjustments': ['View', 'Reconcile'],
'support-center': ['View', 'Respond', 'Close Ticket', 'Reopen Ticket'],
'user-management': ['View', 'Create', 'Edit', 'Deactivate', 'Activate'],
'role-permission': ['View', 'Create', 'Edit', 'Assign Permissions', 'Delete'],
'permission-management': ['View', 'Create', 'Edit', 'Delete'],
'payment-gateway': ['View', 'Configure'],
'system-configuration': ACTION_SETS.CRUD,
'hmo': ['View', 'Create', 'Edit', 'Deactivate', 'Activate'],
};

const SUPERADMIN_TAB_MAP: Record<
  string,
  { name: string; key: string; actions: string[] }[]
> = {
'subscription-management': [
    {
      name: 'Track Subscriptions',
      key: 'subscription-track',
      actions: ACTION_SETS.CRUD,
    },
    {
      name: 'Set Pricing',
      key: 'subscription-pricing',
      actions: ACTION_SETS.CRUD,
    },
    {
      name: 'View Payments',
      key: 'subscription-payments',
      actions: ACTION_SETS.CRUD,
    },
    {
      name: 'Reports',
      key: 'subscription-reports',
      actions: ACTION_SETS.CRUD,
    },
  ],

}
const HMO_TAB_MAP: Record<
  string,
  { name: string; key: string; actions: string[] }[]
> = {
  catalog: [
    {
      name: 'HBP',
      key: 'catalog-hbp',
      actions: ['Create', 'View', 'Edit', 'View Audit Log'],
    },
    {
      name: 'Services Codes',
      key: 'catalog-services',
      actions: ['View', 'Create', 'Delete'],
    },
    {
      name: 'Drug Formulary',
      key: 'catalog-drug',
      actions: ACTION_SETS.CRUD,
    },
  ],

  program: [
    {
      name: 'Programmes',
      key: 'programmes',
      actions: ['View', 'Edit', 'Create'],
    },
    {
      name: 'Plans',
      key: 'plans',
      actions: ['View', 'Edit', 'Create', 'Deactivate'],
    },
  ],
  pricing: [
    {
      name: 'Capitations',
      key: 'pricing-capitations',
      actions: ACTION_SETS.CRUD,
    },
    {
      name: 'Premium Schemas',
      key: 'pricing-premium-schemas',
      actions: ACTION_SETS.CRUD,
    },
    {
      name: 'Payments',
      key: 'pricing-payments',
      actions: ACTION_SETS.CRUD,
    },
  ],

  'formal-enrollee': [
    {
      name: 'Principal',
      key: 'formal-enrollee-principal',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Dependent',
      key: 'formal-enrollee-dependent',
      actions: ['Create', 'View', 'Edit', 'Deactivate', 'Transfer'],
    },
  ],

  'informal-enrollee': [
    {
      name: 'Principal',
      key: 'informal-enrollee-principal',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Dependent',
      key: 'informal-enrollee-dependent',
      actions: ['Create', 'View', 'Edit', 'Deactivate', 'Transfer'],
    },
  ],
  'gifship-enrollee': [
    {
      name: 'Individual (GIFSHIP)',
      key: 'gifship-enrollee-individual',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Family (GIFSHIP)',
      key: 'gifship-enrollee-family',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Group (GIFSHIP)',
      key: 'gifship-enrollee-group',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
  ],

  'vgp-enrollee': [
    {
      name: 'Pregnant Women (VGP)',
      key: 'vgp-enrollee-pregnant-women',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Children (VGP)',
      key: 'vgp-enrollee-children',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Elderly (60+)',
      key: 'vgp-enrollee-elderly',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Persons with Disabilities (PWD)',
      key: 'vgp-enrollee-pwd',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
  ],

  'private-enrollee': [
    {
      name: 'Individual',
      key: 'private-enrollee-individual',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Family',
      key: 'private-enrollee-family',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Corporate',
      key: 'private-enrollee-corporate',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'SME / Group',
      key: 'private-enrollee-sme-group',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
    {
      name: 'Special',
      key: 'private-enrollee-special',
      actions: ACTION_SETS.ENROLLE_CRUD,
    },
  ],

  'program-provider': [
  {
    name: 'Program Transfers',
    key: 'program-provider-transfers',
    actions: ACTION_SETS.APPROVAL_FLOW,
  },
  {
    name: 'Provider Changes',
    key: 'program-provider-changes',
    actions: ACTION_SETS.APPROVAL_FLOW,
  },
  {
    name: 'Suspensions / Reactivations',
    key: 'program-provider-suspensions',
    actions: ACTION_SETS.SUSPENSION_FLOW,
  },
  {
    name: 'De-registrations',
    key: 'program-provider-deregistrations',
    actions: ACTION_SETS.VIEW_ONLY,
  },
],

'capitation': [
  {
    name: 'Capitation Schemas',
    key: 'capitation-schemas',
    actions: ['View', 'Edit', 'Delete'],
  },
  {
    name: 'Capitation Cycles',
    key: 'capitation-cycles',
    actions: ACTION_SETS.VIEW_ONLY, // or ACTION_SETS.VIEW_ONLY
  },
  {
    name: 'Capitation Batches',
    key: 'capitation-batches',
    actions: ACTION_SETS.VIEW_ONLY,
  },
  {
    name: 'Capitation Payments',
    key: 'capitation-payments',
    actions: ['View', 'Advice'], // special action
  },
],

'claims-financing-provider-payments': [
  {
    name: 'Claims Payables',
    key: 'claims-payables',
    actions: ACTION_SETS.VIEW_ONLY,
  },
  {
    name: 'Provider Payment Batches',
    key: 'provider-payment-batches',
    actions: ACTION_SETS.VIEW_ONLY,
  },
  {
    name: 'Provider Settlements',
    key: 'provider-settlements',
    actions: ['View', 'Advice'], // mixed actions
  },
  {
    name: 'Reversals & Adjustments',
    key: 'reversals-adjustments',
    actions: ACTION_SETS.VIEW_ONLY,
  },
],

'premium-contribution': [
  {
    name: 'Premium Schema',
    key: 'premium-schema',
    actions: ['View', 'Edit', 'Delete'], // no Create
  },
  {
    name: 'Contribution',
    key: 'contribution',
    actions: ACTION_SETS.VIEW_ONLY,
  },
  {
    name: 'Premium Payments',
    key: 'premium-payments',
    actions: ['View', 'Receipt', 'External Payment'],
  },
  {
    name: 'Employer & Group Contributions',
    key: 'employer-contributions',
    actions: [
      'Employees',
      'Upload Payroll',
      'Record Payment',
      'Reminder',
    ],
  },
],
};
function getActions(key: string): PermissionAction[] {
  const actions = MODULE_ACTION_MAP[key] || DEFAULT_ACTIONS;

  return actions.map((a) => ({
    name: a,
    enabled: false,
  }));
}

function buildNode(item: any, role: string): PermissionNode {

  const name = getMenuName(role, item.key, item.name);

 const tabs =
  ROLE_TAB_MAP[role]?.[item.key]?.map((tab) => ({
    name: tab.name,
    key: tab.key,
    actions: tab.actions.map((a) => ({
      name: a,
      enabled: false,
    })),
  })) || [];

  const actions = getActions(item.key).filter(a =>
    (MODULE_ACTION_MAP[item.key] || []).includes(a.name)
  );

  return {
    name, //  overridden here
    key: item.key,
    actions,

    children: (item.submenu || []).map((child: any) =>
      buildNode(child, role)
    ),

    tabs,
  };
}

export function buildPermissionTree(
  navItems: MenuItem[],
  role: string
): PermissionNode[] {
  return navItems.map((item) => buildNode(item, role));
}
function getMenuName(role: string, key: string, defaultName: string) {
  return MenuNameOverrideMap[role]?.[key] || defaultName;
}

export const ROLE_TAB_MAP: Record<
  string,
  Record<string, TabConfig[]>
> = {
  hmo: HMO_TAB_MAP,

  superadmin: SUPERADMIN_TAB_MAP,

  payer: {},
};
// export function buildPermissionTree(navItems: MenuItem[]): PermissionNode[] {
//   return navItems.map(module => ({
//     name: module.name,
//     key: module.key,
//     actions: getActions(module.key),

//     children: module.submenu?.map(sub => ({
//       name: sub.name,
//       key: sub.key,
//       actions: getActions(sub.key),

//       children: (sub as any).submenu?.map((child: any) => ({
//         name: child.name,
//         key: child.key,
//         actions: getActions(child.key)
//       })) || []

//     })) || []
//   }));
// }
export const ROLES = {
  HMO: 'hmo',
  PROVIDER: 'provider',
  SUPERADMIN: 'superadmin',
  SUPER_ADMIN: 'super_admin',
  PAYER: 'payer',
  INSUREE: 'insuree',
  NHIA: 'nhia',
} as const;

export const ACTIONS = {
  user: ['view', 'create', 'edit', 'activate', 'deactivate'],
  role: ['view', 'create', 'edit', 'assign-permissions'],
  permission: ['view', 'create', 'edit', 'delete'],
  view: ['view'],
} as const;
export const build = (
  role: string,
  module: string,
  path: string,   //  instead of "resource"
  actions: readonly string[]
) => {
  return actions.map(
    (action) => `portal.web.tamiin.${role}.${module}/${path}/${action}`
  );

  
};

export const buildMany = (
  role: string,
  module: string,
  paths: readonly string[],
  actions: readonly string[]
) => {
  return paths.flatMap((path) =>
    actions.map(
      (action) => `portal.web.tamiin.${role}.${module}/${path}/${action}`
    )
  );
};

export const buildGrouped = (
  role: string,
  module: string,
  groups: Record<string, readonly string[]>,
  actions: readonly string[]
) => {
  return Object.values(groups).flatMap((paths) =>
    paths.flatMap((path) =>
      actions.map(
        (action) => `portal.web.tamiin.${role}.${module}/${path}/${action}`
      )
    )
  );
};